import { MapMarker } from '@wehunt/map'
import { useTranslation } from '../../i18n'
import { isBarkingAt, visibleMessagesAt } from '../../lib/replayEvents'
import { markersAt } from '../../lib/replayInterpolate'
import type { ReplayBundle, ReplayMarker, ReplayMessage } from '../../lib/replayTypes'
import { COLORS } from '../../lib/theme'
import { BarkingDogIcon, DogFacePin, replayPinSrc } from './replayIcons'
import { ReplayNameChip } from './ReplayNameChip'
import { useReplayParticipantName } from './replayParticipantName'

export interface ReplayMarkerLayerProps {
  bundle: ReplayBundle
  cursor: number
  hiddenParticipants: ReadonlySet<string>
  /** False when the Dog barking event type is switched off in the filters. */
  showBarking: boolean
  /** How long a bark stays lit, in hunt-time milliseconds. */
  transientWindowMs: number
  /** False when the Messages event type is switched off in the filters. */
  showMessages: boolean
  /**
   * The current playback speed. A message's window scales with it, so that the
   * same message is on screen for the same readable moment however fast the
   * replay is running — see `messageWindowMs`.
   */
  speed: number
}

/** How far above the dog's own anchor point the barking card sits, on top of the
 *  bottom-anchor `MapMarker` already applies. */
const BARK_OFFSET_PX = 26

/** How far above the sender's own anchor point a chat bubble sits — clear of
 *  their name chip and pin, which are centred on the point. It does not try to
 *  clear a barking card: that belongs to a DOG's marker, never a hunter's, so
 *  the two are only ever near each other when a handler stands with their dog,
 *  and no offset can help there. The z-index below is what settles that case. */
const MESSAGE_OFFSET_PX = 64

/**
 * How many lines of a message are shown.
 *
 * A replay bubble is a glance, not a reader, and nothing caps a message's length
 * on the way in — one pasted paragraph would otherwise cover the terrain the
 * replay exists to show. Six lines is around the point where a bubble stops
 * being readable at a glance anyway, and the clamp adds its own ellipsis so a
 * truncated message does not pretend to be whole.
 */
const MESSAGE_MAX_LINES = 6

/**
 * Hunter and dog markers with their name chips, and the barking indicator above a
 * dog that is baying.
 *
 * DOM rather than canvas: these are a handful of nodes per frame, and keeping them as
 * elements is what lets the chips render real text and a widget test assert on who is
 * on the map.
 *
 * Markers keep their size at every zoom — only the terrain scales. A marker that grew
 * with zoom would swallow the trail it sits on at close range.
 */
export function ReplayMarkerLayer({
  bundle,
  cursor,
  hiddenParticipants,
  showBarking,
  transientWindowMs,
  showMessages,
  speed,
}: ReplayMarkerLayerProps) {
  const markers = markersAt(bundle, cursor, hiddenParticipants)
  // The markers are handed on rather than looked up again: a sender with no
  // marker has nowhere to put a bubble, so passing them in is what makes that
  // rule structural instead of a second interpolation pass and a check.
  const bubbles = showMessages ? visibleMessagesAt(bundle, cursor, speed, markers) : []

  return (
    <div data-testid="replay-marker-layer">
      {markers.map((marker) => (
        <Marker key={marker.participant.id} marker={marker} />
      ))}
      {showBarking &&
        markers
          .filter((marker) => isBarkingAt(marker.participant, cursor, transientWindowMs))
          .map((marker) => <BarkingCard key={`bark-${marker.participant.id}`} marker={marker} />)}
      {bubbles.map(({ marker, message }) => (
        <MessageBubble key={`message-${marker.participant.id}`} marker={marker} message={message} />
      ))}
    </div>
  )
}

function Marker({ marker }: { marker: ReplayMarker }) {
  const { participant } = marker
  const { t } = useTranslation('replay')
  const nameOf = useReplayParticipantName()
  // An anonymous carrier is shown as a link holder sees it, even to themselves.
  const isMe = participant.isMe === true && participant.anonymous !== true

  // Centred on the point: the old DOM math positioned this content with `left/top`
  // at the point and `transform: translate(-50%, -50%)` — shifting it by half its
  // own width AND height in both axes, so its middle (not an edge) lands on the
  // point. That is exactly `MapMarker`'s `anchor: "center"`.
  //
  // zIndex 3, one below BarkingCard's 4: the old flat-DOM layout gave these the
  // same numbers so a barking card always painted above the marker it belongs
  // to when both sit at the same spot. Element-level z-index is what still
  // guarantees that ordering now that each is its own maplibre marker element.
  return (
    <MapMarker lat={marker.lat} lon={marker.lon} anchor="center" zIndex={3}>
      <div
        data-testid={`replay-marker-${participant.id}`}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <ReplayNameChip me={isMe}>{isMe ? t('me') : nameOf(participant)}</ReplayNameChip>
        {participant.role === 'dog' ? (
          <DogFacePin color={participant.color} />
        ) : (
          <img
            // The stationary variant is a hunter at their stand; the plain one is a
            // hunter on the move. Speed rather than a stored flag, because the archive
            // records movement, not intent.
            src={replayPinSrc((marker.speed ?? 0) > 1 ? 'hunter' : 'hunter_safe')}
            alt=""
            width={28}
            height={29}
            draggable={false}
            style={{ display: 'block' }}
          />
        )}
      </div>
    </MapMarker>
  )
}

/** The mobile "Barking now" indicator: the glyph and the word, on a red card. */
function BarkingCard({ marker }: { marker: ReplayMarker }) {
  const { t } = useTranslation('replay')

  // Bottom-anchored, like `GamePin`: the old DOM math positioned this content with
  // `left/top` at the point and `transform: translate(-50%, -100%) translateY(-BARK_OFFSET_PX)`
  // — a bottom-centre anchor (see `MapMarker`'s `anchor: "bottom"`) plus an extra lift
  // above it. `MapMarker` now owns the bottom-anchor translate on the portal element
  // this content is nested inside, so only the extra lift stays here, or it would
  // double-apply the anchor's own translate.
  //
  // zIndex 4, one above `Marker`'s 3: the barking card must paint above the dog's
  // own marker when the two are co-located — the same relative order the old
  // flat-DOM layout gave them.
  return (
    <MapMarker lat={marker.lat} lon={marker.lon} anchor="bottom" zIndex={4}>
      <div
        role="img"
        aria-label={t('barking.label')}
        data-testid={`replay-barking-${marker.participant.id}`}
        style={{
          transform: `translateY(-${BARK_OFFSET_PX}px)`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          padding: '5px 9px 4px',
          borderRadius: 10,
          background: COLORS.error,
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
        }}
      >
        <BarkingDogIcon size={32} color={COLORS.onError} />
        <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.onError, whiteSpace: 'nowrap' }}>
          {t('barking.now')}
        </span>
      </div>
    </MapMarker>
  )
}

/** A chat message in a white card with a downward tail, over its sender. */
function MessageBubble({ marker, message }: { marker: ReplayMarker; message: ReplayMessage }) {
  // zIndex 5, above `BarkingCard`'s 4 and `Marker`'s 3: when a handler and their
  // baying dog are on the same spot, the thing that was said is the thing that
  // must stay readable — the bark is already saying itself in red underneath.
  const nameOf = useReplayParticipantName()
  return (
    <MapMarker lat={marker.lat} lon={marker.lon} anchor="bottom" zIndex={5}>
      <div
        data-testid={`replay-message-${marker.participant.id}`}
        style={{
          transform: `translateY(-${MESSAGE_OFFSET_PX}px)`,
          position: 'relative',
          maxWidth: 220,
          padding: '7px 10px',
          borderRadius: 10,
          background: COLORS.surface,
          color: COLORS.onSurface,
          fontSize: 13,
          lineHeight: 1.35,
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          // Long words and pasted links must not widen the card past its max and
          // out over the terrain.
          overflowWrap: 'anywhere',
        }}
      >
        <span style={{ display: 'block', fontWeight: 600 }}>{nameOf(marker.participant)}</span>
        {/* Clamped in its own element rather than on the card, so the card keeps
            `overflow: visible` and the tail below is not clipped away with the
            overflowing text. */}
        <span
          style={{
            display: '-webkit-box',
            WebkitLineClamp: MESSAGE_MAX_LINES,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            // The app's composer lets a hunter write a message in several
            // lines; collapsing the breaks would run them into one sentence.
            whiteSpace: 'pre-line',
          }}
        >
          {message.text}
        </span>
        {/* The tail. A bordered zero-size box rather than an SVG, so it inherits
            the card's colour and needs no second asset. */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: '50%',
            bottom: -6,
            marginLeft: -6,
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: `6px solid ${COLORS.surface}`,
          }}
        />
      </div>
    </MapMarker>
  )
}
